import React, { useMemo } from 'react';
import { useToggle, useEffectOnce } from 'react-use';
import map from 'lodash/map';
import replace from 'lodash/replace';
import toString from 'lodash/toString';
import memoize from 'lodash/memoize';
import noop from 'lodash/noop';
import { Menu } from 'antd';
import { EllipsisOutlined, PlusOutlined } from '@ant-design/icons';
import { useCreation } from '@umijs/hooks';

import usePlaylistCrud from './hooks/usePlaylistCrud';
import Badge from '@components/snippets/Badge';
import SidebarItem from '@components/layout/Sidebar/SidebarItem';
import EditPlaylist from '@components/snippets/EditPlaylist';
import { Path } from '../../routes';
import AddPlaylistModal from './AddPlaylistModal';
import PlaylistsIcon from '@assets/img/playlists.svg';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import useGettingPlaylists from './hooks/useGettingPlaylists';
import useCreatePlaylist from './hooks/useCreatePlaylist';
import pathToRegexp from 'path-to-regexp';
import usePlaylistDelete from './hooks/usePlaylistDelete';
import first from 'lodash/first';
import useCopyPlaylist from './hooks/useCopyPlaylist';
import useEditPlaylist from './hooks/useEditPlaylist';
import { useHistory } from 'react-router';
import useGettingPlaylist from './hooks/useGettingPlaylist';

const { SubMenu } = Menu;

function PlaylistsMenu({ openId, routeName, routeId, onSelect, ...props }) {
  const { t } = useTranslation();
  const history = useHistory();
  const [showCreatePlaylist, toggleCreatePlaylist] = useToggle(false);

  const { playlist, onFetchPlaylist } = useGettingPlaylist();
  const { playlists = [], onFetchPlaylists } = useGettingPlaylists();
  const { onSubmit } = useCreatePlaylist({
    onSuccess: ({ id }) => {
      onFetchPlaylists();
      toggleCreatePlaylist();
      history.push(pathToRegexp.compile(Path.PLAYLISTS)({ id }));
    },
  });
  const { onDelete } = usePlaylistDelete({
    onSuccess: () => {
      onFetchPlaylists({
        onSuccess: (playlists) =>
          history.replace(
            pathToRegexp.compile(Path.PLAYLISTS)({
              id: first(playlists).id,
            })
          ),
      });
    },
  });
  const { onCopy } = useCopyPlaylist({
    onSuccess: ({ id }) => {
      onFetchPlaylists();
      history.replace(pathToRegexp.compile(Path.PLAYLISTS)({ id }));
    },
  });
  const { onEditPlaylist } = useEditPlaylist({
    onSuccess: () => {
      onFetchPlaylists();
      onFetchPlaylist(playlist.id);
    },
  });

  useEffectOnce(() => {
    onFetchPlaylists();
  });

  const renderExtra = memoize((id, name, color, rest) => {
    return (
      <EditPlaylist
        name={name}
        onChangeColor={onEditPlaylist(
          id,
          { id, name, color, ...rest },
          'color'
        )}
        onRename={onEditPlaylist(id, { id, name, color, ...rest }, 'name')}
        onCopy={onCopy(id, name)}
        onDelete={onDelete(id)}
        color={color}
        rotate={false}
        className="sidebar__nav-item-extra"
        icon={<EllipsisOutlined className="sidebar__nav-item-extra-icon" />}
      />
    );
  });

  const renderIcon = memoize((color) => {
    return <Badge color={color || 'grey'} />;
  });

  const menu = useCreation(() => {
    return map(playlists, ({ id, name, color, ...rest }) => (
      <SidebarItem
        key={`playlist-item-${id}`}
        className={cn({
          'ant-menu-item-selected':
            routeName === 'playlist' && toString(id) === toString(routeId),
        })}
        id={id}
        icon={renderIcon(color)}
        text={name}
        href={replace(Path.PLAYLISTS, ':id', id)}
        extra={renderExtra(id, name, color, rest)}
      />
    ));
  }, [routeName, routeId, playlists]);

  return (
    <SubMenu
      onTitleClick={onSelect}
      onSelect={noop}
      {...props}
      key="item_1"
      className={cn('sidebar__nav-submenu-title', {
        'ant-menu-item-selected': openId === 'playlist',
      })}
      title={
        <div>
          <PlaylistsIcon className="sidebar__nav-icon" width={16} height={16} />
          <span className="sidebar__nav-item-text">{t('playlists')}</span>
        </div>
      }
    >
      <AddPlaylistModal
        t={t}
        onSubmit={onSubmit}
        visible={showCreatePlaylist}
        toggle={toggleCreatePlaylist}
      />
      <SidebarItem
        id="playlists-create"
        key="playlist-new"
        className="sidebar__nav--create"
        icon={
          <PlusOutlined
            className="sidebar__nav-icon--plus"
            width={12}
            height={12}
          />
        }
        onClick={toggleCreatePlaylist}
        text={t('create-playlist')}
      />
      {menu}
    </SubMenu>
  );
}

PlaylistsMenu.whyDidYouRender = true;

export default PlaylistsMenu;
